/**
 * Subscription Monetization Interface - Downgrade Modal
 * 
 * Downgrade UX features:
 * - Scheduled downgrade explanation
 * - Effective date display
 * - Current plan retention notice
 * - Cancel downgrade CTA
 * 
 * Goal: Clear expectations, minimize churn by showing what's being lost.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClinicData } from '../../context/ClinicDataContext';
import { useClinicScope } from '../../hooks/useClinicScope';
import { getLocalizedField } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { 
  ArrowDown, AlertTriangle, X, Loader2, Info,
  Zap, Crown, Building2, Clock, CalendarDays,
  CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import api from '../../utils/api';
import { useContent } from '../../hooks/useContent';
import CommercialDeltaPanel from './CommercialDeltaPanel';

// PXC-B.6 — show the live commercial delta panel inside the downgrade modal.
const PXC_B6_QUOTA_DELTA_UX = true;

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const DowngradeModal = ({
  isOpen,
  onClose,
  fromPackage,
  toPackage,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const { clinicUser } = useClinicData();
  const { clinicId: rbacClinicId } = useClinicScope();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // CMS content
  const { content: downgradeNoticeContent } = useContent('downgrade_no_refund_notice');
  
  if (!isOpen || !fromPackage || !toPackage) return null;
  
  // Package styling
  const packageConfig = {
    TRIAL: { icon: Clock, color: 'bg-blue-100 text-blue-700', gradient: 'from-blue-500 to-blue-600' },
    STARTER: { icon: Zap, color: 'bg-green-100 text-green-700', gradient: 'from-green-500 to-green-600' },
    PRO: { icon: Crown, color: 'bg-purple-100 text-purple-700', gradient: 'from-purple-500 to-purple-600' },
    ENTERPRISE: { icon: Building2, color: 'bg-amber-100 text-amber-700', gradient: 'from-amber-500 to-amber-600' },
  };
  
  const fromConfig = packageConfig[fromPackage.id || fromPackage] || packageConfig.PRO;
  const toConfig = packageConfig[toPackage.id || toPackage.package_key] || packageConfig.STARTER;
  const FromIcon = fromConfig.icon;
  const ToIcon = toConfig.icon;
  
  // Get package details
  const fromName = getLocalizedField(fromPackage.name, 'en') || fromPackage.displayName || fromPackage;
  const toName = getLocalizedField(toPackage.name, 'en') || toPackage.package_name;
  const fromLimits = fromPackage.limits || {};
  const toLimits = toPackage.limits || {};
  
  // Calculate what will be lost
  const lossList = [];
  if ((fromLimits.leads_per_month || 0) > (toLimits.leads_per_month || 0) && toLimits.leads_per_month !== -1) {
    lossList.push(`Leads reduced from ${fromLimits.leads_per_month === -1 ? 'Unlimited' : fromLimits.leads_per_month} to ${toLimits.leads_per_month}`);
  }
  if ((fromLimits.sales_reps || 0) > (toLimits.sales_reps || 0) && toLimits.sales_reps !== -1) {
    lossList.push(`Included users reduced from ${fromLimits.sales_reps === -1 ? 'Unlimited' : fromLimits.sales_reps} to ${toLimits.sales_reps}`);
  }
  
  const fromFeatures = fromPackage.features || {};
  const toFeatures = toPackage.features || {};
  
  if (fromFeatures.campaigns_enabled && !toFeatures.campaigns_enabled) {
    lossList.push('Campaigns');
  }
  if (fromFeatures.leads_enabled && !toFeatures.leads_enabled) {
    lossList.push('Leads Management');
  }
  if (fromFeatures.priority_support && !toFeatures.priority_support) {
    lossList.push('Priority Support');
  }
  
  // Handle downgrade confirmation
  const handleConfirm = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const clinicId = rbacClinicId || clinicUser?.clinicId;
      
      if (!clinicId) {
        setError('Clinic ID not found. Please re-login.');
        setIsLoading(false);
        return;
      }
      
      const { data } = await api.post(`/subscription/clinic/${clinicId}/downgrade`, {
        new_package_key: toPackage.id || toPackage.package_key,
      });
      
      setSuccess(true);
      
      // Delay close to show success message
      setTimeout(() => {
        onConfirm?.(data);
        onClose?.();
      }, 2000);
      
    } catch (err) {
      console.error('[DowngradeModal] Error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Success state
  if (success) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-in fade-in zoom-in-95">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-100 rounded-full flex items-center justify-center">
            <CalendarDays className="h-8 w-8 text-amber-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {t('downgrade.scheduled', 'Downgrade Scheduled')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t('downgrade.scheduledDesc', 'Your {{from}} plan remains active until the end of your billing period. You\'ll be moved to {{to}} after that.', {
              from: fromName,
              to: toName,
            })}
          </p>
          <p className="text-sm text-gray-500">
            {t('common.redirecting', 'Redirecting...')}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <ArrowDown className="h-5 w-5" />
              {t('downgrade.title', 'Downgrade Plan')}
            </h2>
            <button 
              onClick={onClose}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              data-testid="close-downgrade-modal"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-5">
          {/* Plan Change Summary */}
          <div className="flex items-center justify-center gap-4">
            <div className="text-center">
              <div className={`w-14 h-14 mx-auto rounded-xl ${fromConfig.color} flex items-center justify-center mb-2`}>
                <FromIcon className="h-7 w-7" />
              </div>
              <p className="text-sm font-medium text-gray-900">{fromName}</p>
              <p className="text-xs text-gray-500">{t('downgrade.current', 'Current')}</p>
            </div>
            
            <ArrowDown className="h-8 w-8 text-amber-500" />
            
            <div className="text-center">
              <div className={`w-14 h-14 mx-auto rounded-xl ${toConfig.color} flex items-center justify-center mb-2`}>
                <ToIcon className="h-7 w-7" />
              </div>
              <p className="text-sm font-medium text-gray-700">{toName}</p>
              <p className="text-xs text-amber-600">{t('downgrade.new', 'New')}</p>
            </div>
          </div>

          {/* PXC-B.6 — Commercial delta panel */}
          {PXC_B6_QUOTA_DELTA_UX && (
            <CommercialDeltaPanel
              fromPackage={fromPackage}
              toPackage={toPackage}
              mode="downgrade"
            />
          )}
          
          {/* Effective Date Notice */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex items-start gap-3">
              <CalendarDays className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-blue-800">
                  {t('downgrade.effectiveDate', 'Effective at End of Billing Period')}
                </p>
                <p className="text-sm text-blue-700">
                  {downgradeNoticeContent?.body || t('downgrade.effectiveDateDesc', 'Your current {{plan}} plan will remain active until your next billing date. No partial refunds.', {
                    plan: fromName,
                  })}
                </p>
              </div>
            </CardContent>
          </Card>
          
          {/* What You'll Lose */}
          {lossList.length > 0 && (
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4">
                <h4 className="font-semibold text-red-800 flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4" />
                  {t('downgrade.whatYouLose', "What You'll Lose")}
                </h4>
                <ul className="space-y-2">
                  {lossList.map((item, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-red-700">
                      <XCircle className="h-4 w-4 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          
          {/* What You Keep */}
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                <CheckCircle className="h-4 w-4" />
                {t('downgrade.whatYouKeep', "What You'll Keep")}
              </h4>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  {toLimits.leads_per_month === -1 ? 'Unlimited' : toLimits.leads_per_month || 10} leads per month
                </li>
                <li className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  {toLimits.sales_reps === -1 ? 'Unlimited' : toLimits.sales_reps || 1} included users
                </li>
                <li className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle className="h-4 w-4 flex-shrink-0" />
                  {t('downgrade.allData', 'All your existing data and history')}
                </li>
              </ul>
            </CardContent>
          </Card>
          
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={onClose}
              disabled={isLoading}
            >
              {t('downgrade.keepPlan', 'Keep {{plan}}', { plan: fromName })}
            </Button>
            <Button 
              variant="destructive"
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              onClick={handleConfirm}
              disabled={isLoading}
              data-testid="confirm-downgrade-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('downgrade.processing', 'Processing...')}
                </>
              ) : (
                <>
                  <ArrowDown className="h-4 w-4 mr-2" />
                  {t('downgrade.confirm', 'Schedule Downgrade')}
                </>
              )}
            </Button>
          </div>
          
          {/* Notice */}
          <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1">
            <Info className="h-3 w-3" />
            {t('downgrade.cancelAnytime', 'You can cancel this downgrade anytime before your billing date')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DowngradeModal;
